import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  Check, 
  ArrowLeft, 
  Phone,
  Shield,
  Star,
  Zap,
  Crown,
  Building2,
  ExternalLink
} from 'lucide-react';

const PaymentPage = () => {
  const navigate = useNavigate();
  const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

  const packages = [
    {
      id: 'interview_lite',
      name: 'Interview Lite',
      price: 99,
      type: 'One-time',
      icon: Zap,
      description: 'Quick business assessment',
      features: ['AI-guided interview', 'Personalized recommendations', 'Email report']
    },
    {
      id: 'bldr_starter',
      name: 'BLDR Starter',
      price: 99,
      type: 'Monthly',
      icon: Star,
      description: 'Essential business foundation',
      features: ['Business Formation', 'EIN Registration', 'Basic Support']
    },
    {
      id: 'bldr_pro',
      name: 'BLDR Pro',
      price: 199,
      type: 'Monthly',
      icon: Crown,
      description: 'Complete development suite',
      features: ['Everything in Starter', 'Financial Setup', 'Marketing Foundation'],
      popular: true
    },
    {
      id: 'bldr_elite',
      name: 'BLDR Elite',
      price: 299,
      type: 'Monthly',
      icon: Building2,
      description: 'Full-service acceleration',
      features: ['Everything in Pro', 'Legal Package', 'CFO Advisory']
    }
  ];

  // Redirect to Square checkout
  const handleCheckout = () => {
    window.location.href = BLDR_URL;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">Cz</span>
              </div>
              <span className="font-bold text-gray-900">Companeze</span>
              <span className="text-blue-900 text-sm font-semibold">BLDR</span>
            </div>
            <a href="tel:5104224451" className="flex items-center gap-2 text-gray-600 hover:text-blue-900">
              <Phone className="w-4 h-4" />
              (510) 422-4451
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <Badge className="mb-4 bg-blue-900 text-white">Secure Checkout</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h1>
          <p className="text-gray-600">Select a package to get started with Companeze BLDR</p>
        </div>

        {/* Package Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map((pkg) => (
            <Card 
              key={pkg.id}
              className={`border-2 transition-all hover:shadow-lg ${
                pkg.popular ? 'border-blue-900 shadow-xl' : 'border-gray-200'
              }`}
            >
              {pkg.popular && (
                <div className="bg-amber-500 text-white text-center text-xs font-semibold py-1">
                  MOST POPULAR
                </div>
              )}
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center mb-4">
                  <pkg.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{pkg.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{pkg.description}</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-blue-900">${pkg.price}</span>
                  <span className="text-gray-500 text-sm">/{pkg.type.toLowerCase()}</span>
                </div>
                <ul className="space-y-2 mb-4">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-blue-900 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Checkout CTA - Redirects to Square on plangen */}
        <div className="max-w-xl mx-auto">
          <Card className="border-2 border-blue-900 shadow-xl">
            <div className="h-2 bg-blue-900"></div>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to Get Started?</h2>
              <p className="text-gray-600 mb-6">
                Click below to proceed to our secure Square checkout
              </p>
              
              <Button
                size="lg"
                className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg mb-4"
                onClick={handleCheckout}
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Proceed to Secure Checkout
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Shield className="w-4 h-4" />
                <span>Secure payment powered by Square</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alternative Contact */}
        <div className="text-center mt-8">
          <p className="text-gray-600">
            Prefer to pay by phone? Call us at{' '}
            <a href="tel:5104224451" className="text-blue-900 font-semibold hover:underline">
              (510) 422-4451
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-blue-900 text-blue-200 py-6 px-6 mt-12">
        <div className="max-w-6xl mx-auto text-center text-sm">
          <p>© 2025 Companeze BLDR. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PaymentPage;
